import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { RichTextDisplay } from './RichTextDisplay';

/** Approximate height of one line for `text-sm` with `leading-relaxed`. */
const LINE_HEIGHT_PX = 22.75;

interface CollapsibleRichTextProps {
  content: string;
  maxLines?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const CollapsibleRichText: React.FC<CollapsibleRichTextProps> = ({
  content,
  maxLines = 5,
  className = '',
  style,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canCollapse, setCanCollapse] = useState(false);
  const collapsedMaxHeight = Math.round(LINE_HEIGHT_PX * maxLines);

  useLayoutEffect(() => {
    setExpanded(false);
  }, [content]);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) {
      return;
    }

    const measure = () => {
      setCanCollapse(element.scrollHeight > collapsedMaxHeight + 1);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [content, collapsedMaxHeight]);

  if (!content) {
    return null;
  }

  return (
    <div className="w-full min-w-0">
      <div
        ref={contentRef}
        className={expanded ? undefined : 'overflow-hidden'}
        style={expanded ? undefined : { maxHeight: collapsedMaxHeight }}
      >
        <RichTextDisplay
          content={content}
          className={`text-sm leading-relaxed break-words ${className}`.trim()}
          style={style}
        />
      </div>
      {canCollapse && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="mt-2 flex w-full items-center justify-center rounded py-1 transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--color-text-muted)';
          }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse note' : 'Expand note'}
        >
          <ChevronDown
            className="h-4 w-4 transition-transform duration-200"
            style={{ transform: expanded ? 'rotate(180deg)' : undefined }}
          />
        </button>
      )}
    </div>
  );
};
